import { Button, List, Modal, Progress } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { FcCancel } from 'react-icons/fc';
import './UploadProgressModal.scss';

export interface FileManagerUploadProgress {
  [fileName: string]: number | undefined;
}

interface UploadProgressModalProps {
  uploadProgress: FileManagerUploadProgress | null;
  onCancel?: () => void;
}

// Shared upload/reupload progress modal for FileManager (dark-themed).
// Keeps antd's default opaque mask, unlike UploadModal/ReuploadModal, so it
// visibly dims the still-open dragger modal behind it.
const UploadProgressModal = ({ uploadProgress, onCancel }: UploadProgressModalProps) => {
  const progressByFile = uploadProgress ?? {};
  const fileIds = Object.keys(progressByFile);
  const completed = Object.values(progressByFile).filter((progress) => progress === 100).length;
  // Once every file has landed, registration is in flight and cancelling is
  // refused by the hook — reflect that instead of offering a dead button.
  const allComplete = fileIds.length > 0 && completed === fileIds.length;

  return (
    <Modal
      open={fileIds.length > 0}
      closable={false}
      maskClosable={false}
      destroyOnClose
      footer={
        onCancel ? (
          <Button className="fm-upload-progress__cancel-btn" onClick={onCancel} disabled={allComplete}>
            Cancel Upload
          </Button>
        ) : null
      }
      className="fm-progress-modal"
    >
      <List
        header={<div className="fm-upload-progress__header">{`Upload Queue: ${completed}/${fileIds.length} Complete`}</div>}
        itemLayout="horizontal"
        dataSource={fileIds}
        renderItem={(fileId) => {
          const percent = progressByFile[fileId] || 0;
          const isDone = percent === 100;
          return (
            <List.Item className="fm-upload-progress__item">
              <div className="fm-upload-progress__row">
                <div className="fm-upload-progress__info">
                  <div className="fm-upload-progress__meta">
                    <div className="fm-upload-progress__title">File ID: {fileId}</div>
                    <div className="fm-upload-progress__desc">Progress: {percent}%</div>
                  </div>
                  {isDone ? <CheckCircleOutlined className="fm-upload-progress__check" /> : <FcCancel className="fm-upload-progress__cancel" />}
                </div>
                <Progress percent={percent} status={isDone ? 'success' : 'active'} showInfo={false} className="fm-upload-progress__bar" />
              </div>
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default UploadProgressModal;
